'use client';

import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { URLConfig, ConfigurationDetailResponse } from '@/types/urlConfig';
import { i18n } from '@/config/i18n';
import { Breadcrumbs } from '@/components/ui-components';

// ============================================================================
// Types
// ============================================================================

export interface ConfigItem {
  title: string;
  value: string | React.ReactNode;
}

export interface SiteConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  configs?: ConfigItem[];
  // Legacy support for scraper configs
  urlConfig?: URLConfig;
  configDetail?: ConfigurationDetailResponse;
}

// ============================================================================
// Styled Components
// ============================================================================

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 1000;
  padding-top: 60px;
  padding-right: 48px;
  animation: ${({ $isOpen }) => ($isOpen ? 'fadeIn 0.2s ease' : 'none')};
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  max-width: 550px;
  width: 100%;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideInFromRight 0.3s ease;
  
  @keyframes slideInFromRight {
    from {
      transform: translateX(20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
  }
`;

const BreadcrumbWrapper = styled.div`
   gap:16px;    
  margin-bottom: 16px;
  nav {
    border-radius: 0;
  }
`;

const BreadcrumbSeparator = styled.div`
  max-width: 100%;
  height: 1px;
  background: #6E6E6E;
  margin: 16px 24px 0;
`;

const BreadcrumbTitle = styled.h2`
  font-size: 14px;
  font-weight: 400;
  color: #6E6E6E;
  font-family: 'Open Sans', sans-serif;
  margin: 0;
  padding-left: 24px;
  margin-top: -8px;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #EC221F;
  flex-shrink: 0;

  
  &:hover {
    color: #dc2626;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px 24px;
  overflow-y: auto;
  max-height: calc(100vh - 140px);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const ConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 0 16px;
  gap: 48px;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ConfigLabel = styled.div`
  font-size: 12px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  color: #6E6E6E;
  flex: 0 0 140px;
  min-width: 150px;
  
  @media (max-width: 768px) {
    flex: 0 0 120px;
  }
`;

const ConfigValue = styled.div`
  font-size: 12px;
  font-family: 'Open Sans', sans-serif;
  color: #101018;
  flex: 1;
  text-align: left;
  word-break: break-word;
`;

const CriticalElementsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-start;
`;

const ElementTag = styled.span`
  display: inline-block;
  padding: 4px;
  background: #F5F5F5;
  border-radius: 4px;
  font-size: 12px;
  color: #101018;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * SiteConfigurationModal Component
 * 
 * Displays a modal with blurred background showing all site configuration parameters
 * for a URL configuration.
 * 
 * @component
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * 
 * <SiteConfigurationModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   urlConfig={urlConfig}
 * />
 * ```
 */
export const SiteConfigurationModal: React.FC<SiteConfigurationModalProps> = ({
  isOpen,
  onClose,
  title,
  configs,
  urlConfig,
  configDetail
}) => {
  // Parse critical elements string to array
  const criticalElementsArray = useMemo(() => {
    // Use urlConfig as primary source
    if (urlConfig?.criticalElements) {
      // Try to parse as JSON array first
      try {
        const parsed = JSON.parse(urlConfig.criticalElements);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // If not JSON, try splitting by comma or newline
        const split = urlConfig.criticalElements
          ?.split(/[,\n]/)
          .map(el => el?.trim())
          .filter(el => el?.length > 0);
        return split;
      }
    }
    
    // Fallback to configDetail if urlConfig doesn't have it
    if (configDetail?.configuration?.elements?.critical) {
      return configDetail.configuration.elements.critical;
    }
    
    return [];
  }, [urlConfig?.criticalElements, configDetail]);

  // Get dynamic elements array
  const dynamicElementsArray = useMemo(() => {
    // Use urlConfig as primary source
    if (urlConfig?.dynamicElements) {
      try {
        const parsed = JSON.parse(urlConfig.dynamicElements);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        const split = urlConfig.dynamicElements
          ?.split(/[,\n]/)
          .map(el => el?.trim())
          .filter(el => el?.length > 0);
        return split;
      }
    }
    
    // Fallback to configDetail
    if (configDetail?.configuration?.elements?.dynamic) {
      return configDetail.configuration.elements.dynamic;
    }
    
    return [];
  }, [urlConfig?.dynamicElements, configDetail]);

  // Handle Escape key and body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Use provided title or fallback to urlConfig.clientName for backward compatibility
  const modalTitle = title || urlConfig?.clientName || 'Site Configuration';
  
  const breadcrumbItems = [
    { label: modalTitle }
  ];

  const closeButtonSection = (
    <CloseButton onClick={onClose} aria-label="Close modal">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M6 18L18 6M6 6l12 12" />
      </svg>
    </CloseButton>
  );

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <BreadcrumbWrapper>
          <Breadcrumbs 
            items={breadcrumbItems}
            accentColor="#FB6F2A"
            rightSection={closeButtonSection}
          />
          <BreadcrumbTitle>{i18n.pageScrapper.siteConfiguration.title}</BreadcrumbTitle>
          <BreadcrumbSeparator />
        </BreadcrumbWrapper>
        
        <ModalBody>
          {/* Render generic configs if provided */}
          {configs && configs.length > 0 ? (
            configs.map((config, index) => (
              <ConfigRow key={index}>
                <ConfigLabel>{config.title}</ConfigLabel>
                <ConfigValue>{config.value}</ConfigValue>
              </ConfigRow>
            ))
          ) : (
            /* Legacy scraper config rendering for backward compatibility */
            <>
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.createdOn}</ConfigLabel>
                <ConfigValue>
                  {urlConfig?.createdAt || configDetail?.created_at
                    ? new Date(urlConfig?.createdAt || configDetail?.created_at || '').toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </ConfigValue>
              </ConfigRow>
              {(urlConfig?.created_by || configDetail?.created_by) && (
                <ConfigRow>
                  <ConfigLabel>{i18n.pageScrapper.siteConfiguration.createdBy}</ConfigLabel>
                  <ConfigValue>{urlConfig?.created_by || configDetail?.created_by || '-'}</ConfigValue>
                </ConfigRow>
              )} 
              {(urlConfig?.email || configDetail?.configuration?.error_handling?.alert_email) && (
                <ConfigRow>
                  <ConfigLabel>{i18n.pageScrapper.siteConfiguration.emailRecipient}</ConfigLabel>
                  <ConfigValue>{urlConfig?.email || configDetail?.configuration?.error_handling?.alert_email || '-'}</ConfigValue>
                </ConfigRow>
              )}
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.timeout}</ConfigLabel>
                <ConfigValue>{urlConfig?.customTimeout || configDetail?.configuration?.settings?.timeout || 0}s</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.retryAttempts}</ConfigLabel>
                <ConfigValue>{urlConfig?.customRetryAttempts || configDetail?.configuration?.settings?.retries || 0}</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.waitAfterLoad}</ConfigLabel>
                <ConfigValue>{urlConfig?.waitAfterLoad || configDetail?.configuration?.settings?.wait_time || 0}s</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.elementTemplate}</ConfigLabel>
                <ConfigValue>{urlConfig?.elementTemplate || configDetail?.configuration?.settings?.element_template || '-'}</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>Schedule Type</ConfigLabel>
                <ConfigValue>{urlConfig?.scheduleType || configDetail?.configuration?.schedule?.type || '-'}</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.fcpThresholds}</ConfigLabel>
                <ConfigValue>{urlConfig?.fcpThreshold || configDetail?.configuration?.thresholds?.fcp || 0}ms</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.lcpThresholds}</ConfigLabel>
                <ConfigValue>{urlConfig?.lcpThreshold || configDetail?.configuration?.thresholds?.lcp || 0}ms</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.ttfbThresholds}</ConfigLabel>
                <ConfigValue>{urlConfig?.ttfbThreshold || configDetail?.configuration?.thresholds?.ttfb || 0}ms</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.tbtThresholds}</ConfigLabel>
                <ConfigValue>{configDetail?.configuration?.thresholds?.tbt || 0}ms</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.clsThresholds}</ConfigLabel>
                <ConfigValue>{configDetail?.configuration?.thresholds?.cls || 0}</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.siThresholds}</ConfigLabel>
                <ConfigValue>{configDetail?.configuration?.thresholds?.si || 0}ms</ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.screenshots}</ConfigLabel>
                <ConfigValue>
                  {(urlConfig?.takeScreenshots ?? configDetail?.configuration?.settings?.screenshots)
                    ? i18n.pageScrapper.siteConfiguration.enabled 
                    : i18n.pageScrapper.siteConfiguration.disabled}
                </ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.checkResources}</ConfigLabel>
                <ConfigValue>
                  {(urlConfig?.checkResources ?? configDetail?.configuration?.settings?.check_resources)
                    ? i18n.pageScrapper.siteConfiguration.enabled 
                    : i18n.pageScrapper.siteConfiguration.disabled}
                </ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.monitorAjax}</ConfigLabel>
                <ConfigValue>
                  {(urlConfig?.monitorAjaxCalls ?? configDetail?.configuration?.settings?.monitor_ajax)
                    ? i18n.pageScrapper.siteConfiguration.enabled 
                    : i18n.pageScrapper.siteConfiguration.disabled}
                </ConfigValue>
              </ConfigRow>
              
              <ConfigRow>
                <ConfigLabel>{i18n.pageScrapper.siteConfiguration.criticalElements}</ConfigLabel>
                <ConfigValue>
                  {criticalElementsArray.length > 0 ? (
                    <CriticalElementsContainer>
                      {criticalElementsArray.map((element, index) => (
                        <ElementTag key={index}>{element}</ElementTag>
                      ))}
                    </CriticalElementsContainer>
                  ) : (
                    '-'
                  )}
                </ConfigValue>
              </ConfigRow>

              {dynamicElementsArray.length > 0 && (
                <ConfigRow>
                  <ConfigLabel>Dynamic Elements</ConfigLabel>
                  <ConfigValue>
                    <CriticalElementsContainer>
                      {dynamicElementsArray.map((element, index) => (
                        <ElementTag key={index}>{element}</ElementTag>
                      ))}
                    </CriticalElementsContainer>
                  </ConfigValue>
                </ConfigRow>
              )}
            </>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default SiteConfigurationModal;

